import { useCallback, useMemo, useRef } from 'react'
import {
  collection,
  doc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { setDoc, updateDoc, deleteDoc, getDocs } from '../lib/firestore'
import { db } from '../lib/firebase'
import { friendshipsCol } from '../lib/converters'
import { friendPair, friendshipPairId } from '../lib/friendId'
import { useFirestoreCollection } from './useFirestore'
import { runMutation } from '../lib/firestoreMutation'
import { track, type AnalyticsEvents } from '../lib/analytics'

type FriendAddedEntryPoint = AnalyticsEvents['friend_added']['entry_point']
import type { Friendship, UserProfile } from '../types'
import { fbt } from 'fbtee'
import { log } from '../lib/logger'

export interface FriendshipsResult {
  friendIds: string[]
  friendIdSet: Set<string>
  incoming: Friendship[]
  outgoing: Friendship[]
  loading: boolean
  sendRequest: (otherUid: string, source: 'group' | 'search' | 'profile' | 'share_link') => Promise<void>
  acceptRequest: (otherUid: string) => Promise<void>
  acceptInvite: (inviterUid: string, entryPoint?: FriendAddedEntryPoint) => Promise<void>
  removeFriend: (otherUid: string) => Promise<void>
}

const EMPTY: Friendship[] = []

export async function findUserByFriendCode(friendCode: string): Promise<UserProfile | null> {
  const snap = await getDocs(
    query(collection(db, 'users'), where('friendCode', '==', friendCode.toUpperCase())) as Query<
      Omit<UserProfile, 'uid'>
    >,
  )
  if (snap.empty) return null
  const d = snap.docs[0]
  return { uid: d.id, ...d.data() }
}

export function useFriendships(userId: string | null): FriendshipsResult {
  const ref = userId
    ? (query(friendshipsCol(), where('userIds', 'array-contains', userId)) as Query<Friendship>)
    : null
  const { data, loading } = useFirestoreCollection<Friendship>(ref)

  const { friendIds, incoming, outgoing } = useMemo(() => {
    if (!userId) return { friendIds: [] as string[], incoming: EMPTY, outgoing: EMPTY }
    const friends: string[] = []
    const inc: Friendship[] = []
    const out: Friendship[] = []
    for (const f of data) {
      const other = f.userIds.find(u => u !== userId)
      if (!other) continue
      if (f.status === 'accepted') friends.push(other)
      else if (f.requestedBy === userId) out.push(f)
      else inc.push(f)
    }
    return { friendIds: friends, incoming: inc, outgoing: out }
  }, [data, userId])

  const acceptsInFlight = useRef(0)

  const sendRequest = useCallback(
    async (otherUid: string, source: 'group' | 'search' | 'profile' | 'share_link') => {
      if (!userId || otherUid === userId) return
      await runMutation(fbt("Couldn't send friend request", 'Error toast when sending a friend request fails'), async () => {
        await setDoc(doc(friendshipsCol(), friendshipPairId(userId, otherUid)), {
          userIds: friendPair(userId, otherUid),
          requestedBy: userId,
          status: 'pending',
          createdAt: serverTimestamp(),
        } as never)
        track('friend_request_sent', { entry_point: source })
      })
    },
    [userId],
  )

  const acceptRequest = useCallback(
    async (otherUid: string) => {
      if (!userId) return
      await runMutation(fbt("Couldn't accept request", 'Error toast when accepting a friend request fails'), async () => {
        const pairId = friendshipPairId(userId, otherUid)
        // GlitchTip 8713779: an accept was denied; the rules only allow it on a pending
        // request the viewer didn't send, so log what the client believed when it tapped.
        const seen = data.find(f => f.id === pairId)
        const concurrent = acceptsInFlight.current
        acceptsInFlight.current++
        try {
          await updateDoc(doc(db, 'friendships', pairId), {
            status: 'accepted',
            acceptedAt: serverTimestamp(),
          })
        } catch (err) {
          log('friends', 'acceptRequest failed', {
            docInSnapshot: !!seen,
            status: seen?.status ?? null,
            requestedByViewer: seen ? seen.requestedBy === userId : null,
            concurrentAccepts: concurrent,
          })
          throw err
        } finally {
          acceptsInFlight.current--
        }
        track('friend_request_accepted', {})
        track('social_connected', { entry_point: 'friend' })
      })
    },
    [userId, data],
  )

  const acceptInvite = useCallback(
    async (inviterUid: string, entryPoint: FriendAddedEntryPoint = 'invite_link') => {
      if (!userId || inviterUid === userId) return
      await runMutation(fbt("Couldn't add friend", 'Error toast when adding a friend fails'), async () => {
        await setDoc(doc(friendshipsCol(), friendshipPairId(userId, inviterUid)), {
          userIds: friendPair(userId, inviterUid),
          requestedBy: inviterUid,
          status: 'accepted',
          createdAt: serverTimestamp(),
          acceptedAt: serverTimestamp(),
        } as never)
        track('friend_added', { entry_point: entryPoint })
        track('social_connected', { entry_point: 'friend' })
      })
    },
    [userId],
  )

  const removeFriend = useCallback(
    async (otherUid: string) => {
      if (!userId) return
      await runMutation(fbt("Couldn't remove friend", 'Error toast when removing a friend fails'), async () => {
        await deleteDoc(doc(db, 'friendships', friendshipPairId(userId, otherUid)))
      })
    },
    [userId],
  )

  const friendIdSet = useMemo(() => new Set(friendIds), [friendIds])

  return { friendIds, friendIdSet, incoming, outgoing, loading, sendRequest, acceptRequest, acceptInvite, removeFriend }
}
